import type { BaseEventData } from '../base.event';

type BaseMooEventData = BaseEventData;

/** Sentinel value used by callers when an id slot is empty (e.g. fresh-chat
 *  conversation). LogRocket's track payload schema does not accept `null` so
 *  domain payloads use empty-string sentinels for nullable id fields. */
type AgentRefPayload = BaseMooEventData & {
  agentId: string;
};

type AgentConversationPayload = BaseMooEventData & {
  agentId: string;
  conversationId: string;
};

type MessageSentPayload = AgentConversationPayload & {
  contextCount: number;
};

type ImprovePromptCompletedPayload = AgentRefPayload & {
  ok: boolean;
};

type FeedbackSubmittedPayload = BaseMooEventData & {
  messageId: string;
  /** `'withdrawn'` represents the re-click-to-clear gesture (transport
   *  receives `type: null` but the analytics payload uses a string sentinel
   *  because LogRocket does not accept `null` values). */
  type: 'up' | 'down' | 'brilliant' | 'withdrawn';
  hasComment: boolean;
};

type ContextChangePayload = BaseMooEventData & {
  contextType: 'entity' | 'document' | 'selection' | 'screen';
};

type AgentSelectedPayload = BaseMooEventData & {
  agentId: string;
};

type ConversationOpenedPayload = BaseMooEventData & {
  conversationId: string;
  agentId: string;
};

type ConversationIdPayload = BaseMooEventData & {
  conversationId: string;
};

/**
 * Canonical Moo-chat event names for {@link Analytics.track}.
 *
 * Implemented as a `const` object so values are true string-literal types and
 * the same object doubles as the runtime namespace for callers, e.g.
 * `analytics.track(MooAnalyticsEvents.MESSAGE_SENT, …)`.
 *
 * Strings follow the `<surface>.<snake_case_action>` convention used by other
 * domains (e.g. {@link QuoteAnalyticsEvents}).
 */
export const MooAnalyticsEvents = {
  MESSAGE_SENT: 'moo.message_sent',
  MESSAGE_CANCELLED: 'moo.message_cancelled',
  IMPROVE_PROMPT_CLICKED: 'moo.improve_prompt_clicked',
  IMPROVE_PROMPT_COMPLETED: 'moo.improve_prompt_completed',
  FEEDBACK_SUBMITTED: 'moo.feedback_submitted',
  CONTEXT_ATTACHED: 'moo.context_attached',
  CONTEXT_REMOVED: 'moo.context_removed',
  CONTEXT_PINNED: 'moo.context_pinned',
  CONTEXT_UNPINNED: 'moo.context_unpinned',
  AGENT_SELECTED: 'moo.agent_selected',
  CONVERSATION_OPENED: 'moo.conversation_opened',
  CONVERSATION_DELETED: 'moo.conversation_deleted',
  CONVERSATION_NEW: 'moo.conversation_new',
} as const;

export type MooAnalyticsEventName =
  (typeof MooAnalyticsEvents)[keyof typeof MooAnalyticsEvents];

export type MooAnalyticsEventMap = {
  [MooAnalyticsEvents.MESSAGE_SENT]: MessageSentPayload;
  [MooAnalyticsEvents.MESSAGE_CANCELLED]: AgentConversationPayload;
  [MooAnalyticsEvents.IMPROVE_PROMPT_CLICKED]: AgentRefPayload;
  [MooAnalyticsEvents.IMPROVE_PROMPT_COMPLETED]: ImprovePromptCompletedPayload;
  [MooAnalyticsEvents.FEEDBACK_SUBMITTED]: FeedbackSubmittedPayload;
  [MooAnalyticsEvents.CONTEXT_ATTACHED]: ContextChangePayload;
  [MooAnalyticsEvents.CONTEXT_REMOVED]: ContextChangePayload;
  [MooAnalyticsEvents.CONTEXT_PINNED]: ContextChangePayload;
  [MooAnalyticsEvents.CONTEXT_UNPINNED]: ContextChangePayload;
  [MooAnalyticsEvents.AGENT_SELECTED]: AgentSelectedPayload;
  [MooAnalyticsEvents.CONVERSATION_OPENED]: ConversationOpenedPayload;
  [MooAnalyticsEvents.CONVERSATION_DELETED]: ConversationIdPayload;
  [MooAnalyticsEvents.CONVERSATION_NEW]: BaseMooEventData;
};
